import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowLeft, CheckCircle, Camera, Facebook, Instagram } from 'lucide-react';
import imageCompression from 'browser-image-compression';
import { useAuth } from '../../context/AuthContext';
import { imageService } from '../../services/supabaseService';
import { useNotifications } from '../shared/useNotifications';
import { DISCIPLINE_TABS } from '../../utils/constants';
import './ClubRegister.css';
import './AuthShared.css';

const ClubRegister = ({ onBackToHome, onBackToLogin }) => {
  const navigate = useNavigate();
  const { signUp, signOut } = useAuth();
  const { success: showSuccess, error: showError } = useNotifications();
  const [step, setStep] = useState('form');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    passwordConfirm: '',
    phone: '',
    location: '',
    disciplines: [],
    facebook_url: '',
    instagram_url: '',
    twitter_url: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const toggleDiscipline = (key) => {
    setFormData(prev => ({
      ...prev,
      disciplines: prev.disciplines.includes(key)
        ? prev.disciplines.filter(d => d !== key)
        : [...prev.disciplines, key]
    }));
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const compressed = await imageCompression(file, {
        maxSizeMB: 0.15,
        maxWidthOrHeight: 400,
        useWebWorker: true
      });
      setLogoFile(compressed);
      const reader = new FileReader();
      reader.onloadend = () => setLogoPreview(reader.result);
      reader.readAsDataURL(compressed);
    } catch (err) {
      console.error('Error comprimiendo logo:', err);
      showError('Error al procesar la imagen');
    }
  };

  const validateForm = () => {
    if (!formData.name.trim()) { setError('El nombre del club es obligatorio'); return false; }
    if (!formData.email.trim()) { setError('El correo es obligatorio'); return false; }
    if (!/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(formData.email.trim())) { setError('Correo inválido'); return false; }
    if (!formData.password) { setError('La contraseña es obligatoria'); return false; }
    if (formData.password.length < 6) { setError('La contraseña debe tener al menos 6 caracteres'); return false; }
    if (formData.password !== formData.passwordConfirm) { setError('Las contraseñas no coinciden'); return false; }
    if (formData.disciplines.length === 0) { setError('Selecciona al menos una disciplina'); return false; }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!validateForm()) { setLoading(false); return; }

    try {
      const { data: authDataWithProfile, error: authErrorWithProfile } = await signUp(
        formData.email.trim(),
        formData.password,
        'club',
        {
          name: formData.name.trim(),
          email: formData.email.trim().toLowerCase(),
          phone: formData.phone.trim() || null,
          location: formData.location.trim() || null,
          disciplines: formData.disciplines.length > 0 ? formData.disciplines : [],
          facebook_url: formData.facebook_url || null,
          instagram_url: formData.instagram_url || null,
          twitter_url: formData.twitter_url || null,
          is_active: false,
          approval_status: 'pending_approval',
        }
      );

      if (authErrorWithProfile) {
        setError(authErrorWithProfile);
        setLoading(false);
        return;
      }

      const clubData = authDataWithProfile?.profile;
      if (!clubData) {
        setError('Error guardando el perfil del club. Intenta nuevamente.');
        setLoading(false);
        return;
      }

      // 3. Upload logo if provided
      if (logoFile && clubData?.id) {
        try {
          const uploadResult = await imageService.uploadImage(logoFile, 'club', clubData.id);
          if (!uploadResult.success) {
            console.error('Error subiendo logo del club:', uploadResult.error);
          }
        } catch (uploadErr) {
          console.error('Error en upload de logo:', uploadErr);
        }
      }

      // 4. Send notification email to admin
      try {
        await fetch('/api/send-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'club-registration',
            clubName: formData.name.trim(),
            clubEmail: formData.email.trim(),
            clubPhone: formData.phone.trim(),
            clubLocation: formData.location.trim(),
            disciplines: formData.disciplines
          })
        });
      } catch (emailErr) {
        console.warn('No se pudo enviar notificación al admin:', emailErr);
      }

      showSuccess('✅ Registro enviado para aprobación');

      // Cerrar sesión: el club está pendiente de aprobación,
      // no debe quedar logueado (evita error de "perfil no encontrado" en dashboard)
      try { await signOut(); } catch (_) {}

      setStep('success');
    } catch (err) {
      console.error('Error en registro:', err);
      setError('Error al procesar el registro. Intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'success') {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-success">
            <CheckCircle size={48} />
            <h2>¡Registro Enviado!</h2>
            <p>Tu solicitud de registro ha sido enviada para aprobación.</p>
            <p style={{ fontSize: '12px', color: '#9ca3af' }}>El equipo de CopaGimnasta revisará tu solicitud y recibirás un correo electrónico cuando sea aprobada. Una vez aprobado, podrás acceder al panel de tu club.</p>
            <button type="button" onClick={onBackToLogin} className="auth-btn-primary" style={{ marginTop: 20 }}>
              Ir al Inicio de Sesión
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <button type="button" onClick={onBackToLogin} className="auth-back" aria-label="Volver">
          <ArrowLeft size={18} /> Volver
        </button>

        <div className="auth-header">
          <img src="/letracopagimnasta.png" alt="CopaGimnasta" className="auth-logo" onClick={() => navigate('/')} />
          <h2>Registrar Club</h2>
          <p>Crea una cuenta para tu club</p>
        </div>

        {error && (
          <div className="auth-alert auth-alert-error">
            <AlertCircle size={18} />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          {/* Logo Upload */}
          <div className="club-logo-upload">
            <label htmlFor="club-logo-input" className="club-logo-upload-area">
              {logoPreview ? (
                <img src={logoPreview} alt="Logo preview" className="club-logo-preview" />
              ) : (
                <div className="club-logo-placeholder">
                  <Camera size={24} />
                  <span>Logo</span>
                </div>
              )}
              <input
                id="club-logo-input"
                type="file"
                accept="image/*"
                onChange={handleLogoChange}
                style={{ display: 'none' }}
                disabled={loading}
              />
            </label>
            <span className="club-upload-hint">Opcional · JPG/PNG · Máx 150KB</span>
          </div>

          <div className="auth-form-group">
            <label>Nombre del Club *</label>
            <input type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder="Nombre del club" required disabled={loading} />
          </div>

          <div className="auth-form-group">
            <label>Correo Electrónico *</label>
            <input type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="club@correo.com" required disabled={loading} />
          </div>

          <div className="club-form-row">
            <div className="auth-form-group">
              <label>Contraseña *</label>
              <input type="password" name="password" value={formData.password} onChange={handleInputChange} placeholder="Mínimo 6 caracteres" required disabled={loading} />
            </div>
            <div className="auth-form-group">
              <label>Confirmar *</label>
              <input type="password" name="passwordConfirm" value={formData.passwordConfirm} onChange={handleInputChange} placeholder="Repite contraseña" required disabled={loading} />
            </div>
          </div>

          <div className="club-form-section-label">Disciplinas *</div>
          <p className="club-discipline-hint">Selecciona las disciplinas que maneja tu club</p>
          <div className="club-discipline-grid">
            {DISCIPLINE_TABS.map(tab => {
              const isSelected = formData.disciplines.includes(tab.key);
              return (
                <button
                  key={tab.key}
                  type="button"
                  className={`club-discipline-chip ${isSelected ? 'active' : ''}`}
                  onClick={() => toggleDiscipline(tab.key)}
                  disabled={loading}
                >
                  <img src={tab.logo} alt={tab.name} className="club-discipline-logo" />
                  <span className="club-discipline-name">{tab.name}</span>
                  <span className="club-discipline-full">{tab.fullName}</span>
                </button>
              );
            })}
          </div>

          <div className="club-form-row">
            <div className="auth-form-group">
              <label>Teléfono</label>
              <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} placeholder="Contacto" disabled={loading} />
            </div>
            <div className="auth-form-group">
              <label>Ubicación</label>
              <input type="text" name="location" value={formData.location} onChange={handleInputChange} placeholder="Ciudad, País" disabled={loading} />
            </div>
          </div>

          <div className="club-form-section-label">Redes Sociales <span className="club-optional-tag">Opcional</span></div>

          <div className="auth-form-group club-form-social">
            <label><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 512 512" fill="currentColor"><path d="M512 256C512 114.6 397.4 0 256 0S0 114.6 0 256c0 120 82.7 220.8 194.2 248.5V330.1h-58.5v-74h58.5v-56.4c0-57.7 34.4-89.6 87-89.6 25.2 0 51.5 4.5 51.5 4.5v56.6h-29c-28.5 0-37.4 17.7-37.4 35.8V256h63.9l-10.2 74h-53.7v174.4C429.3 476.8 512 376 512 256z"/></svg> Facebook</label>
            <input type="url" name="facebook_url" value={formData.facebook_url} onChange={handleInputChange} placeholder="https://facebook.com/tuclub" disabled={loading} />
          </div>

          <div className="auth-form-group club-form-social">
            <label><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 448 512" fill="currentColor"><path d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9S160.5 370.9 224.1 370.9 339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1S3.8 127.9 2 163.8c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z"/></svg> Instagram</label>
            <input type="url" name="instagram_url" value={formData.instagram_url} onChange={handleInputChange} placeholder="https://instagram.com/tuclub" disabled={loading} />
          </div>

          <div className="auth-form-group club-form-social">
            <label><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 512 512" fill="currentColor"><path d="M389.2 48h70.6L305.6 224.2 487 464H345L233.7 318.6 106.5 464H35.8l164.9-188.5L26.8 48h145.6l100.5 132.9zm-24.8 373.8h39.1L151.1 88h-42z"/></svg> X (Twitter)</label>
            <input type="url" name="twitter_url" value={formData.twitter_url} onChange={handleInputChange} placeholder="https://x.com/tuclub" disabled={loading} />
          </div>

          <button type="submit" className="auth-btn-primary" style={{ marginTop: 8 }} disabled={loading}>
            {loading ? 'Registrando...' : 'Registrar Club'}
          </button>
        </form>
        <p className="auth-copyright">
          © {new Date().getFullYear()} CopaGimnasta. Todos los derechos reservados.
        </p>
      </div>
    </div>
  );
};

export default ClubRegister;
